import { IconBrandWhatsapp, IconMail, IconPhone } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { ContactForm } from "./contact-form";
import { MarketingLocaleSwitcher } from "./locale-switcher";
import { MarketingMark } from "./marketing-mark";
import { useMarketingPageMeta } from "./use-marketing-page-meta";

/**
 * Public marketing surface for ZonaClara.
 *
 * Lives outside RequireAuth so anonymous visitors see the pitch; signed-in
 * users keep a path into the console without losing the landing page.
 * Visual tokens come from `theme/marketing.css` (`.marketing-root`), separate
 * from the admin Mantine theme.
 */
export function MarketingHomePage() {
  const { t } = useTranslation("marketing");
  const { status } = useAuth();
  const signedIn = status === "authenticated";

  useMarketingPageMeta("documentTitle", "metaDescription", "/");

  return (
    <div className="marketing-root marketing-page min-h-dvh">
      <section className="marketing-hero relative min-h-[min(94dvh,54rem)] overflow-hidden">
        <img
          src="/marketing/hero-lab-storage.jpg"
          alt={t("heroImageAlt")}
          className="marketing-hero-photo absolute inset-0 h-full w-full object-cover object-center"
          width={1920}
          height={1080}
          fetchPriority="high"
        />
        <div className="marketing-hero-scrim absolute inset-0" aria-hidden="true" />

        <header className="relative z-10 flex items-center justify-between gap-4 px-5 py-6 sm:px-8 lg:px-12">
          <a href="/" className="marketing-lockup">
            <MarketingMark className="size-8 sm:size-9" />
            <span className="marketing-brand-mark text-lg text-white sm:text-2xl">
              {t("brand")}
            </span>
          </a>
          <nav
            className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 sm:gap-x-4"
            aria-label={t("brand")}
          >
            <a href="#usos" className="marketing-nav-link hidden lg:inline">
              {t("navUses")}
            </a>
            <Link to="/equipos" className="marketing-nav-link hidden lg:inline">
              {t("navEquipment")}
            </Link>
            <a href="#contact" className="marketing-nav-link hidden lg:inline">
              {t("navContact")}
            </a>
            <MarketingLocaleSwitcher tone="onDark" />
            {signedIn ? (
              <Link
                to="/dashboard"
                className="marketing-cta marketing-cta-primary px-3 py-2 text-sm"
              >
                {t("navDashboard")}
              </Link>
            ) : (
              <Link to="/login" className="marketing-cta marketing-cta-on-dark px-3 py-2 text-sm">
                {t("navLogin")}
              </Link>
            )}
          </nav>
        </header>

        <div className="marketing-hero-copy relative z-10 flex min-h-[min(78dvh,46rem)] flex-col justify-start px-5 pb-16 pt-6 sm:px-8 sm:pb-20 sm:pt-10 lg:px-12">
          <div className="marketing-hero-panel">
            <h1 className="marketing-display marketing-fade-up marketing-delay-1 text-3xl leading-tight text-white sm:text-4xl">
              {t("heroHeadline")}
            </h1>
            <p className="marketing-fade-up marketing-delay-2 mt-4 max-w-xl text-base text-white/90 sm:text-lg">
              {t("heroLead")}
            </p>
            <ul
              className="marketing-fade-up marketing-delay-2 mt-6 flex flex-wrap gap-2"
              aria-label={t("channelLabel")}
            >
              <li className="marketing-channel">
                <IconBrandWhatsapp size={18} aria-hidden="true" />
                {t("channelWhatsapp")}
              </li>
              <li className="marketing-channel">
                <IconMail size={18} aria-hidden="true" />
                {t("channelEmail")}
              </li>
              <li className="marketing-channel">
                <IconPhone size={18} aria-hidden="true" />
                {t("channelCall")}
              </li>
            </ul>
            <div className="marketing-fade-up marketing-delay-3 mt-8 flex flex-wrap gap-3">
              <a href="#contact" className="marketing-cta marketing-cta-primary">
                {t("ctaContact")}
              </a>
              <a href="#usos" className="marketing-cta marketing-cta-on-dark">
                {t("navUses")}
              </a>
            </div>
          </div>
        </div>
      </section>

      <main id="main">
        <section className="bg-surface">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
            <hr className="marketing-rule" />
            <h2 className="marketing-display mt-5 text-2xl text-ink sm:text-3xl">
              {t("problemTitle")}
            </h2>
            <p className="mt-3 max-w-2xl text-ink-muted">{t("problemLead")}</p>
            <ul className="mt-12 grid gap-10 sm:grid-cols-3 sm:gap-8">
              <li className="marketing-step">
                <h3 className="text-base font-semibold text-ink">{t("problem1Title")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{t("problem1Body")}</p>
              </li>
              <li className="marketing-step">
                <h3 className="text-base font-semibold text-ink">{t("problem2Title")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{t("problem2Body")}</p>
              </li>
              <li className="marketing-step">
                <h3 className="text-base font-semibold text-ink">{t("problem3Title")}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{t("problem3Body")}</p>
              </li>
            </ul>
          </div>
        </section>

        <section
          aria-labelledby="intelligent-company"
          className="border-t border-border-subtle bg-surface-muted"
        >
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
            <hr className="marketing-rule" />
            <h2
              id="intelligent-company"
              className="marketing-display mt-5 text-2xl text-ink sm:text-3xl"
            >
              {t("intelligentTitle")}
            </h2>
            <p className="mt-3 max-w-2xl text-ink-muted">{t("intelligentLead")}</p>
            {/* WebKit drops the implicit list role when list-style is none. */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ol className="marketing-flow" role="list" aria-label={t("intelligentFlowLabel")}>
              {(
                [
                  ["intelligent1", undefined],
                  ["intelligent2", undefined],
                  ["intelligent3", undefined],
                  ["intelligent4", "call"],
                ] as const
              ).map(([key, tone], index) => (
                <li key={key} className="marketing-flow-step" data-tone={tone}>
                  <span className="marketing-flow-index" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="marketing-flow-label">{t(key)}</h3>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section
          id="usos"
          className="marketing-section-alt scroll-mt-8 border-y border-border-subtle"
        >
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
            <hr className="marketing-rule" />
            <h2 className="marketing-display mt-5 text-2xl text-ink sm:text-3xl">
              {t("usesTitle")}
            </h2>
            <p className="mt-3 max-w-2xl text-ink-muted">{t("usesLead")}</p>
            <ul className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              <UseCase
                src="/marketing/use-case-warehouse.jpg"
                alt={t("useWarehouseAlt")}
                title={t("useWarehouseTitle")}
                body={t("useWarehouseBody")}
              />
              <UseCase
                src="/marketing/use-case-tanks.jpg"
                alt={t("useTankAlt")}
                title={t("useTankTitle")}
                body={t("useTankBody")}
              />
              <UseCase
                src="/marketing/use-case-leak.jpg"
                alt={t("useLeakAlt")}
                title={t("useLeakTitle")}
                body={t("useLeakBody")}
              />
              <UseCase
                src="/marketing/use-case-door.jpg"
                alt={t("useDoorAlt")}
                title={t("useDoorTitle")}
                body={t("useDoorBody")}
              />
              <UseCase
                src="/marketing/use-case-current.jpg"
                alt={t("useCurrentAlt")}
                title={t("useCurrentTitle")}
                body={t("useCurrentBody")}
              />
              <UseCase
                src="/marketing/use-case-lab.jpg"
                alt={t("useLabAlt")}
                title={t("useLabTitle")}
                body={t("useLabBody")}
              />
            </ul>
            <div className="mt-12">
              <Link to="/equipos" className="marketing-cta marketing-cta-primary">
                {t("ctaEquipment")}
              </Link>
            </div>
          </div>
        </section>

        <section className="marketing-band">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
            <p className="marketing-kicker text-white/70">{t("brand")}</p>
            <h2 className="marketing-display mt-3 max-w-3xl text-2xl text-white sm:text-4xl">
              {t("promiseTitle")}
            </h2>
            <p className="marketing-band-muted mt-4 max-w-3xl text-base sm:text-lg">
              {t("promiseLead")}
            </p>
            <h3 className="mt-8 text-base font-semibold text-white">{t("help3Title")}</h3>
            <p className="marketing-band-muted mt-2 max-w-3xl text-sm leading-relaxed">
              {t("help3Body")}
            </p>
          </div>
        </section>

        <section id="contact" className="scroll-mt-8 bg-surface">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:gap-16">
            <div>
              <hr className="marketing-rule" />
              <h2 className="marketing-display mt-5 text-2xl text-ink sm:text-3xl">
                {t("contactTitle")}
              </h2>
              <p className="mt-3 text-ink-muted">{t("contactLead")}</p>
              <p className="mt-8 max-w-md text-sm leading-relaxed text-ink-muted">
                <span className="font-semibold text-ink">{t("honestTitle")}. </span>
                {t("honestBody")}
              </p>
            </div>
            <div className="marketing-contact-panel p-5 sm:p-7">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border-subtle bg-[var(--marketing-navy-deep)] px-4 py-7 text-center text-xs text-white/65 sm:px-6">
        {t("footerNote")}
      </footer>
    </div>
  );
}

function UseCase({
  src,
  alt,
  title,
  body,
}: {
  src: string;
  alt: string;
  title: string;
  body: string;
}) {
  return (
    <li>
      <img
        src={src}
        alt={alt}
        className="marketing-use-photo aspect-[4/3] w-full object-cover"
        width={1200}
        height={900}
        loading="lazy"
      />
      <h3 className="mt-4 text-base font-semibold text-ink">{title}</h3>
      <p className="mt-1 text-sm text-ink-muted">{body}</p>
    </li>
  );
}
